import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { functions } from './firebase';
import { httpsCallable } from "firebase/functions";
import './CreditsPage.css';
import { usePostHog } from 'posthog-js/react';
import discordLogo from './assets/logos/Classic_RTS_Logo.png';
import EmailRegistrationCard from './components/EmailRegistrationCard';
import StravaClubCard from './components/StravaClubCard';

function CreditsPage({ user }) {
    const { t } = useTranslation();
    const [monthlyUsage, setMonthlyUsage] = useState({ currentCount: 0, monthlyLimit: 10, remaining: 10 });
    const [isLoadingUsage, setIsLoadingUsage] = useState(true);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [waitlistEmail, setWaitlistEmail] = useState('');
    const [isSubmittingWaitlist, setIsSubmittingWaitlist] = useState(false);
    const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
    const [waitlistError, setWaitlistError] = useState('');
    const posthog = usePostHog();


    // State for watermarked generations
    const [watermarkedGenerations, setWatermarkedGenerations] = useState([]);
    const [isLoadingWatermarked, setIsLoadingWatermarked] = useState(true);
    
    // State for redeemed coupons
    const [redeemedCoupons, setRedeemedCoupons] = useState([]);
    const [isLoadingCoupons, setIsLoadingCoupons] = useState(true);

    useEffect(() => {
        if (user) {
            loadMonthlyUsage();
            loadWatermarkedGenerations();
            loadRedeemedCoupons();
        } else {
            setIsLoadingUsage(false);
            setIsLoadingWatermarked(false);
            setIsLoadingCoupons(false);
        }
    }, [user]);

    const loadMonthlyUsage = async () => {
        if (!user) return;

        setIsLoadingUsage(true);
        try {
            const getUserMonthlyUsageFunc = httpsCallable(functions, 'getUserMonthlyUsage');
            const result = await getUserMonthlyUsageFunc();
            setMonthlyUsage(result.data);
        } catch (error) {
            console.error("Error loading monthly usage:", error);
        } finally {
            setIsLoadingUsage(false);
        }
    };

    const loadWatermarkedGenerations = async () => {
        if (!user) return;

        setIsLoadingWatermarked(true);
        try {
            const getWatermarkedFunc = httpsCallable(functions, 'getWatermarkedGenerations');
            const result = await getWatermarkedFunc({ limit: 10 });
            setWatermarkedGenerations(result.data.generations || []);
        } catch (error) {
            console.error("Error loading watermarked generations:", error);
        } finally {
            setIsLoadingWatermarked(false);
        }
    };

    const loadRedeemedCoupons = async () => {
        if (!user) return;

        setIsLoadingCoupons(true);
        try {
            const getRedeemedCouponsFunc = httpsCallable(functions, 'getRedeemedCoupons');
            const result = await getRedeemedCouponsFunc();
            if (result.data.success) {
                setRedeemedCoupons(result.data.coupons || []);
            }
        } catch (error) {
            console.error("Error loading redeemed coupons:", error);
        } finally {
            setIsLoadingCoupons(false);
        }
    };



    const handleWaitlistSubmit = async (e) => {
        e.preventDefault();
        setIsSubmittingWaitlist(true);
        setWaitlistError('');

        try {
            // Call the joinCreditsWaitlist function
            const joinWaitlistFunc = httpsCallable(functions, 'joinCreditsWaitlist');
            await joinWaitlistFunc({ email: waitlistEmail });
            
            posthog?.capture('waitlist_signup', {
                page: 'credits',
                email: waitlistEmail
            });

            setWaitlistSubmitted(true);
        } catch (error) {
            console.error("Error submitting waitlist:", error);
            setWaitlistError(t('creditsPage.waitlistError'));
        } finally {
            setIsSubmittingWaitlist(false);
        }
    };




    if (!user) {
        return (
            <div className="credits-page">
                <div className="credits-container">
                    <p>{t('common.signInToAccess', { page: t('creditsPage.title') })}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="credits-page">
            <div className="credits-container">
                <h1>{t('creditsPage.title')}</h1>

                {/* Current Credits Section */}
                <div className="credits-section">
                    <h2>
                        <i className="ti ti-coins" style={{ fontSize: '24px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                        {t('creditsPage.yourCredits')}
                    </h2>
                    <p className="section-description">{t('creditsPage.subtitle')}</p>

                    <div className="maps-counter">
                        <div className="maps-info">
                            <span className="maps-badge">{t('credits.freeCredits')}</span>
                        </div>
                        <div className="maps-count">
                            {isLoadingUsage ? (
                                <div className="loading-spinner-small" style={{ margin: '0 20px' }}></div>
                            ) : (
                                <>
                                    <span className="current">{monthlyUsage.remaining}</span>
                                    <span className="separator">{t('credits.remaining')}</span>
                                    {monthlyUsage.bonusCredits > 0 && (
                                        <span className="bonus-credits" style={{ fontSize: '14px', color: 'var(--Gray-600)', marginLeft: '8px' }}>
                                            {t('creditsPage.includesEarned', { count: monthlyUsage.bonusCredits })}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Earn Credits Section */}
                <div className="credits-section">
                    <h2>
                        <i className="ti ti-gift" style={{ fontSize: '24px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                        {t('creditsPage.earnFreeCredits')}
                    </h2>
                    <p className="section-description">{t('creditsPage.earnFreeCreditsDesc')}</p>

                    {/* Email Registration Reward Card */}
                    <EmailRegistrationCard
                        user={user}
                        posthog={posthog}
                        variant="default"
                        onRewardClaimed={() => loadMonthlyUsage()}
                        alwaysVisible={true}
                    />

                    {/* Strava Club Reward Card */}
                    <StravaClubCard
                        user={user}
                        posthog={posthog}
                        onRewardClaimed={() => loadMonthlyUsage()}
                    />

                    {/* Coupon Redemption Card */}
                    <div className="reward-card">
                        <div className="reward-content">
                            <h4>
                                <i className="ti ti-ticket" style={{ marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                                {t('creditsPage.haveACouponCode')}
                            </h4>
                            <p>{t('creditsPage.redeemCouponDesc')}</p>
                            <Link
                                to="/coupon"
                                className="reward-button"
                                style={{ textDecoration: 'none', display: 'inline-block', marginTop: '12px' }}
                            >
                                {t('creditsPage.redeemCoupon')}
                            </Link>
                            
                            {/* Redeemed Coupons History */}
                            {redeemedCoupons.length > 0 && (
                                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--Gray-200, #e5e7eb)' }}>
                                    <h5 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--Gray-900)', marginBottom: '12px' }}>
                                        {t('couponPage.previouslyRedeemed')}
                                    </h5>
                                    {isLoadingCoupons ? (
                                        <div style={{ textAlign: 'center', padding: '12px' }}>
                                            <div className="loading-spinner-small" style={{ margin: '0 auto' }}></div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {redeemedCoupons.slice(0, 3).map((coupon, index) => (
                                                <div key={index} style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '8px 12px',
                                                    backgroundColor: 'var(--Gray-50, #f9fafb)',
                                                    borderRadius: '6px',
                                                    fontSize: '13px'
                                                }}>
                                                    <span style={{ color: 'var(--Gray-700)', fontWeight: '500' }}>
                                                        {coupon.couponName || coupon.code}
                                                    </span>
                                                    <span style={{ color: 'var(--Success-600)', fontWeight: '600' }}>
                                                        +{coupon.credits}
                                                    </span>
                                                </div>
                                            ))}
                                            {redeemedCoupons.length > 3 && (
                                                <Link
                                                    to="/coupon"
                                                    style={{
                                                        fontSize: '13px',
                                                        color: 'var(--Primary-600)',
                                                        textAlign: 'center',
                                                        marginTop: '4px',
                                                        textDecoration: 'none'
                                                    }}
                                                >
                                                    View all {redeemedCoupons.length} redeemed coupons →
                                                </Link>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Share and Earn Credits Card */}
                    <div className="reward-card share-reward-card">
                        <div className="reward-content">
                            <h4>{t('creditsPage.shareEarnTitle')}</h4>
                            <p>{t('creditsPage.shareEarnDesc')}</p>

                            <div style={{
                                marginTop: '16px',
                                marginBottom: '20px',
                                padding: '16px',
                                backgroundColor: 'var(--Gray-50, #f9fafb)',
                                borderRadius: '8px',
                                border: '1px solid var(--Gray-200, #e5e7eb)'
                            }}>
                                <p style={{ fontSize: '14px', color: 'var(--Gray-700)', lineHeight: '1.5', margin: 0 }}>
                                    <strong>{t('creditsPage.howItWorks')}</strong> {t('creditsPage.howItWorksBody')}
                                </p>
                                <p style={{ fontSize: '14px', color: 'var(--Gray-700)', lineHeight: '1.5', marginTop: '12px', marginBottom: 0 }}>
                                    {t('creditsPage.havingTrouble')}{' '}
                                    <a
                                        href="https://discord.gg/fprRUxfyu4"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'var(--Primary-600)', textDecoration: 'underline' }}
                                    >
                                        {t('common.discord')}
                                    </a>
                                    {' '}{t('creditsPage.needHelp')}
                                </p>
                            </div>

                            <Link
                                to="/history"
                                className="reward-button"
                                style={{ textDecoration: 'none', display: 'inline-block' }}
                            >
                                {t('creditsPage.shareMyMapsCta')}
                            </Link>

                            {/* Maps where credits were earned */}
                            {isLoadingWatermarked ? (
                                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                                    <div className="loading-spinner-small" style={{ margin: '0 auto' }}></div>
                                    <p style={{ marginTop: '12px', color: 'var(--Gray-600)', fontSize: '14px' }}>
                                        {t('gallery.loading')}
                                    </p>
                                </div>
                            ) : watermarkedGenerations.length > 0 && (
                                <div style={{ marginTop: '24px' }}>
                                    <h5 style={{ margin: '0 0 12px 0', color: 'var(--Gray-700)', fontSize: '15px', fontWeight: '500' }}>
                                        {t('creditsPage.mapsEarnedFor')}
                                    </h5>
                                    <div className="credited-maps-grid">
                                        {watermarkedGenerations.map((generation) => (
                                            <div key={generation.id} className="credited-map-card">
                                                {generation.generatedImageUrl && (
                                                    <div className="map-thumbnail">
                                                        <img
                                                            src={generation.generatedImageUrl}
                                                            alt={generation.activityName}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                        <div className="photo-placeholder" style={{ display: 'none' }}>
                                                            <i className="ti ti-photo-off"></i>
                                                        </div>
                                                        {generation.creditAwarded && (
                                                            <div className="credit-earned-badge">
                                                                <i className="ti ti-coins"></i>
                                                                +1
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="map-info">
                                                    <h5>{generation.activityName}</h5>
                                                    <div className="map-actions">
                                                        <Link
                                                            to={`/map/${generation.id}`}
                                                            className="view-map-link"
                                                        >
                                                            {t('common.viewMap')}
                                                        </Link>
                                                        {generation.stravaActivityId && (
                                                            <a
                                                                href={`https://www.strava.com/activities/${generation.stravaActivityId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="view-strava-link"
                                                            >
                                                                {t('common.viewOnStrava')}
                                                                <i className="ti ti-external-link"></i>
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Purchase Credits Section */}
                <div className="credits-section">
                    <h2>
                        <i className="ti ti-shopping-cart" style={{ fontSize: '24px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                        {t('creditsPage.purchaseCredits')}
                    </h2>

                    <div className="purchase-credits-card"
                        onClick={() => {
                            setShowPurchaseModal(true);
                            posthog?.capture('purchase_credits_clicked', {
                                page: 'credits',
                                current_credits: monthlyUsage.currentCount,
                                credits_used: monthlyUsage.currentCount,
                                credits_remaining: monthlyUsage.remaining
                            });
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ flex: 1 }}>
                                <h4 style={{
                                    margin: '0 0 8px 0',
                                    color: 'var(--Gray-900, #101828)',
                                    fontSize: '18px',
                                    fontWeight: '600'
                                }}>
                                {t('creditsPage.purchaseCardTitle')}
                                </h4>
                                <p style={{
                                    margin: 0,
                                    color: 'var(--Gray-600, #475467)',
                                    fontSize: '14px'
                                }}>
                                    {t('creditsPage.purchaseCardDesc')}
                                </p>
                            </div>
                            <div className="purchase-button">
                                <i className="ti ti-shopping-cart" style={{ fontSize: '16px' }}></i>
                                {t('creditsPage.buyCreditsCta')}
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Purchase Modal */}
            {showPurchaseModal && (
                <div className="modal-overlay" onClick={() => setShowPurchaseModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setShowPurchaseModal(false)}>
                            <i className="ti ti-x"></i>
                        </button>

                        <h2>{t('creditsPage.purchaseComingSoon')}</h2>

                        <div className="waitlist-section">
                            <p>{t('creditsPage.waitlistDesc')}</p>

                            {!waitlistSubmitted ? (
                                <>
                                    <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
                                        <input
                                            type="email"
                                            placeholder={t('common.enterEmail')}
                                            value={waitlistEmail}
                                            onChange={(e) => setWaitlistEmail(e.target.value)}
                                            required
                                        />
                                        <button type="submit" disabled={isSubmittingWaitlist}>
                                            {isSubmittingWaitlist ? t('common.joining') : t('common.joinWaitlist')}
                                        </button>
                                    </form>
                                    {waitlistError && (
                                        <div style={{ marginTop: '12px', color: 'var(--Error-600)', fontSize: '14px' }}>
                                            {waitlistError}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="waitlist-success">
                                    <i className="ti ti-circle-check"></i>
                                    <p>{t('creditsPage.waitlistThanks', { email: waitlistEmail })}</p>
                                </div>
                            )}
                        </div>

                        <div className="discord-section">
                            <p>{t('creditsPage.discordInvite')}</p>
                            <a
                                href="https://discord.gg/your-discord-invite"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="discord-button"
                            >
                                <img src={discordLogo} alt="Discord" />
                                {t('creditsPage.joinDiscordCta')}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CreditsPage;